import { defineComponent as _defineComponent } from 'vue';
import { faBook, faCaretDown, faDownload, faHome, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import axios from "axios";
import { BAlert, BButton, BDropdown, BDropdownDivider, BDropdownGroup, BDropdownItem, BFormCheckbox, } from "bootstrap-vue";
import { computed, reactive, ref } from "vue";
import { GalaxyApi } from "@/api";
import { Services } from "@/components/Libraries/LibraryFolder/services";
import { deleteSelectedItems } from "@/components/Libraries/LibraryFolder/TopToolbar/delete-selected";
import download from "@/components/Libraries/LibraryFolder/TopToolbar/download";
import mod_import_collection from "@/components/Libraries/LibraryFolder/TopToolbar/import-to-history/import-collection";
import mod_import_dataset from "@/components/Libraries/LibraryFolder/TopToolbar/import-to-history/import-dataset";
import {} from "@/components/SelectionDialog/selectionTypes";
import { useConfig } from "@/composables/config";
import { Toast } from "@/composables/toast";
import { getAppRoot } from "@/onload";
import { useUserStore } from "@/stores/userStore";
import CollectionCreatorModal from "@/components/Collections/CollectionCreatorModal.vue";
import FolderDetails from "@/components/Libraries/LibraryFolder/FolderDetails/FolderDetails.vue";
import LibraryBreadcrumb from "@/components/Libraries/LibraryFolder/LibraryBreadcrumb.vue";
import SearchField from "@/components/Libraries/LibraryFolder/SearchField.vue";
import DirectoryDatasetPicker from "@/components/Libraries/LibraryFolder/TopToolbar/DirectoryDatasetPicker.vue";
import ProgressBar from "@/components/ProgressBar.vue";
import HistoryDatasetPicker from "@/components/SelectionDialog/HistoryDatasetPicker.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'FolderTopBar',
    props: {
        metadata: null,
        selected: null,
        folderId: null,
        unselected: null,
        searchText: null,
        folderContents: null,
        includeDeleted: { type: Boolean },
        isAllSelectedMode: { type: Boolean },
        canAddLibraryItem: { type: Boolean }
    },
    emits: ["newFolder", "refreshTable", "fetchFolderContents", "refreshTableContent", "setBusy", "deleteFromTable", "updateSearch", "update:includeDeleted"],
    setup(__props, { emit }) {
        const props = __props;
        const { config, isConfigLoaded } = useConfig();
        const userStore = useUserStore();
        const modalShow = ref();
        const progress = ref(false);
        const progressNote = ref("");
        const progressStatus = reactive({
            total: 0,
            okCount: 0,
            errorCount: 0,
            runningCount: 0,
        });
        const services = new Services();
        const libraryImportDir = computed(() => isConfigLoaded && config.value?.library_import_dir);
        const allowLibraryPathPaste = computed(() => isConfigLoaded && config.value?.allow_library_path_paste);
        const userLibraryImportDirAvailable = computed(() => isConfigLoaded && config.value?.user_library_import_dir_available);
        const containsFileOrFolder = computed(() => {
            return props.folderContents.find((el) => el.type === "folder" || el.type === "file");
        });
        const canDelete = computed(() => {
            return !!props.metadata.can_modify_folder;
        });
        const datasetManipulation = computed(() => {
            return !!(containsFileOrFolder.value && userStore.currentUser);
        });
        const totalRows = computed(() => {
            return props.metadata?.total_rows ?? 0;
        });
        // Variables for Collection Creation
        const collectionModalType = ref(null);
        const collectionModalShow = ref(false);
        const collectionSelection = ref([]);
        const collectionHistoryId = ref(null);
        function updateSearch(value) {
            emit("updateSearch", value);
        }
        function newFolder() {
            emit("newFolder");
        }
        async function getSelected() {
            if (props.isAllSelectedMode) {
                try {
                    emit("setBusy", true);
                    const selected = await services.getFilteredFolderContents(props.folderId, props.unselected, props.searchText, totalRows.value);
                    emit("setBusy", false);
                    return selected;
                }
                catch (err) {
                    console.error(err);
                }
                finally {
                    emit("setBusy", false);
                }
                return [];
            }
            else {
                return props.selected;
            }
        }
        async function deleteSelected() {
            try {
                const selected = await getSelected();
                deleteSelectedItems(selected, (deletedItem) => emit("deleteFromTable", deletedItem), () => emit("refreshTable"), () => emit("refreshTableContent"));
            }
            catch (err) {
                console.error(err);
            }
        }
        async function findCheckedItems(idOnly = true) {
            const folders = [];
            const datasets = [];
            try {
                const selected = await getSelected();
                selected.forEach((item) => {
                    const selectedItem = idOnly ? item.id : item;
                    item.type === "file" ? datasets.push(selectedItem) : folders.push(selectedItem);
                });
            }
            catch (err) {
                console.error(err);
            }
            return { datasets, folders };
        }
        async function downloadData(format) {
            try {
                const { datasets, folders } = await findCheckedItems();
                if (props.selected.length === 0) {
                    Toast.info("You must select at least one dataset to download");
                    return;
                }
                download(format, datasets, folders);
            }
            catch (err) {
                console.error(err);
            }
        }
        async function importToHistoryModal(isCollection) {
            try {
                const { datasets, folders } = await findCheckedItems(!isCollection);
                const checkedItems = props.selected;
                checkedItems.dataset_ids = datasets;
                checkedItems.folder_ids = folders;
                if (isCollection) {
                    new mod_import_collection.ImportCollectionModal({
                        selected: checkedItems,
                        onCollectionImport: async (collectionType, selection, historyId) => {
                            try {
                                collectionHistoryId.value = historyId;
                                collectionModalType.value = collectionType;
                                collectionSelection.value = selection.models;
                                collectionModalShow.value = true;
                            }
                            catch (err) {
                                console.error(err);
                            }
                        },
                    });
                }
                else {
                    new mod_import_dataset.ImportDatasetModal({
                        selected: checkedItems,
                    });
                }
            }
            catch (err) {
                console.error(err);
            }
        }
        function onAddDatasets(source) {
            modalShow.value = source;
        }
        function resetProgress() {
            progressStatus.total = 0;
            progressStatus.okCount = 0;
            progressStatus.errorCount = 0;
            progressStatus.runningCount = 0;
        }
        async function addDatasets(selectedDatasets, datasetApiCall) {
            resetProgress();
            progress.value = true;
            progressStatus.total = selectedDatasets.length;
            progressNote.value = "Adding datasets to the folder";
            emit("setBusy", true);
            for (const dataset of selectedDatasets) {
                try {
                    progressStatus.runningCount++;
                    await datasetApiCall(dataset);
                    progressStatus.okCount++;
                }
                catch (e) {
                    progressStatus.errorCount++;
                }
                finally {
                    progressStatus.runningCount--;
                }
            }
            if (progressStatus.errorCount > 0) {
                progressNote.value = `Added ${progressStatus.okCount} dataset${progressStatus.okCount > 1 ? "s" : ""}, but failed to add ${progressStatus.errorCount} dataset${progressStatus.errorCount > 1 ? "s" : ""} to the folder`;
            }
            else {
                progressNote.value = `Added ${progressStatus.okCount} dataset${progressStatus.okCount > 1 ? "s" : ""} to the folder`;
            }
            emit("setBusy", false);
            emit("fetchFolderContents");
        }
        function onAddDatasetsFromHistory(selectedDatasets) {
            const datasetApiCall = async (dataset) => {
                const { error } = await GalaxyApi().POST("/api/folders/{folder_id}/contents", {
                    params: {
                        path: { folder_id: props.folderId },
                    },
                    body: {
                        ldda_message: null,
                        from_hda_id: dataset.id,
                    },
                });
                if (error) {
                    throw new Error(error.err_msg);
                }
            };
            addDatasets(selectedDatasets, datasetApiCall);
        }
        function onAddDatasetsDirectory(selectedDatasets) {
            const datasetApiCall = async (dataset) => {
                await axios.post(`${getAppRoot()}api/libraries/datasets`, dataset);
            };
            addDatasets(selectedDatasets, datasetApiCall);
        }
        return { __sfc: true, props, emit, config, isConfigLoaded, userStore, modalShow, progress, progressNote, progressStatus, services, libraryImportDir, allowLibraryPathPaste, userLibraryImportDirAvailable, containsFileOrFolder, canDelete, datasetManipulation, totalRows, collectionModalType, collectionModalShow, collectionSelection, collectionHistoryId, updateSearch, newFolder, getSelected, deleteSelected, findCheckedItems, downloadData, importToHistoryModal, onAddDatasets, resetProgress, addDatasets, onAddDatasetsFromHistory, onAddDatasetsDirectory, faBook, faCaretDown, faDownload, faHome, faPlus, faTrash, FontAwesomeIcon, BAlert, BButton, BDropdown, BDropdownDivider, BDropdownGroup, BDropdownItem, BFormCheckbox, CollectionCreatorModal, FolderDetails, LibraryBreadcrumb, SearchField, DirectoryDatasetPicker, ProgressBar, HistoryDatasetPicker };
    }
});
